<template>
   <p>
      Now that you've installed the ezbot tracker, let's tell it what success
      looks like for your website. This is called setting up a "reward" - it's
      simply telling ezbot when a visitor does something valuable on your site
      (like making a purchase or signing up). You need to set up
      <strong>one</strong> reward for ezbot to work properly.
      <a href="https://docs.ezbot.ai/get-started/strategize/" target="_blank"
         >Learn more about choosing the right reward here</a
      >.
   </p>

   <p>
      If you use Google Tag Manager on your website, we've made this easy for
      you! Just follow our
      <a
         href="https://docs.ezbot.ai/guides/installation/google-tag-manager/"
         target="_blank"
         >simple Google Tag Manager guide</a
      >.
   </p>

   <p>If you're not using Google Tag Manager, you have two simple options:</p>
   <ul>
      <li>
         <strong>Option 1 (Simplest):</strong> If success happens when a visitor
         reaches a specific page (like a "Thank You" or confirmation page), you
         can easily set this up
         <RouterLink
            :to="`/orgs/${route.params.urlSlug}/projects/${projectStore.projectId}/rewards`"
            >in the rewards settings page</RouterLink
         >.
      </li>
      <li>
         <strong>Option 2:</strong> If success is when a visitor does something
         specific (like clicking a button), you'll need to add a small piece of
         code to your website as shown below. Your web developer can help with
         this.
      </li>
   </ul>
   <VCodeBlock
      :code="htmlRewardCode"
      highlightjs
      label="Code for your web developer to add (for Option 2):"
      lang="html"
      theme="github-dark-dimmed"
      copy-button
      :persistent-copy-button="true"
   />
</template>
<script lang="ts" setup>
import { ref } from "vue"
import VCodeBlock from "@wdns/vue-code-block"
import { useProjectStore } from "@/stores/projectStore"
import { useRoute } from "vue-router"

const htmlRewardCode = ref<string>("")
const route = useRoute()
const projectStore = useProjectStore()

htmlRewardCode.value = `
<!-- This code tells ezbot when someone clicks your button -->
<!-- Place this code snippet after ezbot.init() has been called -->
<script>
  // This finds your button (replace 'myButton' with your actual button's ID)
  const button = document.getElementById('myButton');

  // This tells ezbot to count a reward when someone clicks the button
  button.addEventListener('click', function() {
      ezbot.trackRewardEvent({
          key: "button_click",     
          reward: 1,              
          rewardUnits: "count",
      });
  });
<\/script>
`
</script>
